import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { router } from 'expo-router';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useOpenReplyDrafts } from './useOpenReplyDrafts';
import { findOfferedDraft } from '../model/status';
import { lendComposer } from '../state/composerBridge';

import { ROUTES } from '@/constants/routes';
import type { ActionDotPhrase } from '@/features/dot-phrases/model';
import { useAbility } from '@/hooks/useAbility';

export const DRAFT_SHORTCUT = {
  trigger: 'draft',
  label: '.draft',
  description: 'Draft your reply',
  placeholderHint: 'type .draft to draft a reply',
};

/**
 * What the chat mounts the web modal with. It exists from the moment the
 * shortcut is offered, before `.draft` is picked, so the modal animates in when
 * `isOpen` changes rather than appearing already open.
 */
export type ReplyDraftModalState = {
  isOpen: boolean;
};

const NO_PHRASES: ActionDotPhrase[] = [];

type ReplyDraftShortcutOptions = {
  conversationId: string;
  isExpert: boolean;
  composerText: string;
  setComposerText: (text: string) => void;
  /** Runs after a reply lands in the composer, for the chat to grow and focus it. */
  onHandedOff: () => void;
};

/**
 * The `.draft` composer shortcut for one conversation.
 *
 * It is offered only to an expert the server lets read reply drafts, which is
 * the per-expert drafting grant, and always then,
 * whether or not the open drafts have loaded: offering it only once a draft
 * was known meant a `.draft` typed in the first moments of a chat did nothing.
 * What it opens finds the draft itself and shows a spinner meanwhile. The
 * drafts are still requested here, and not at all for anyone else, so the
 * placeholder can mention `.draft` only where a draft exists: an expert who
 * will never get one in a chat is not told about it in every composer.
 *
 * Where a draft exists the chat also shows a chip that opens it, because a
 * typed shortcut is found only by someone who reads the placeholder and types
 * the dot: in testing a literal reader typed "draft" and nothing happened. The
 * chip is the way in for most experts; the phrase stays for those who type.
 *
 * Picking it opens the draft the way the reference assistant opens: a
 * full-screen route on a phone, and a modal over the chat on web. Either way
 * the draft fills this composer through the loan made here, which lasts while
 * the chat is mounted.
 */
export function useReplyDraftShortcut({
  conversationId,
  isExpert,
  composerText,
  setComposerText,
  onHandedOff,
}: ReplyDraftShortcutOptions) {
  const isEnabled = useAbility().can('read', 'ReplyDraft') && isExpert;

  const { data: drafts } = useOpenReplyDrafts({ enabled: isEnabled });

  const hasOfferedDraft = isEnabled && !!findOfferedDraft(drafts ?? [], conversationId);

  const [isOpen, setIsOpen] = useState(false);

  const textRef = useRef(composerText);

  const setTextRef = useRef(setComposerText);

  const onHandedOffRef = useRef(onHandedOff);

  const tokenStartRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    textRef.current = composerText;

    setTextRef.current = setComposerText;

    onHandedOffRef.current = onHandedOff;
  });

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    return lendComposer(conversationId, {
      getText: () => textRef.current,
      setText: (text) => setTextRef.current(text),
      getTokenStart: () => tokenStartRef.current,
      onHandedOff: () => onHandedOffRef.current(),
    });
  }, [conversationId, isEnabled]);

  /**
   * Where the draft lands in the composer: where `.draft` was typed, or with
   * no token, after whatever is already there.
   */
  const open = useCallback(
    (tokenStart: number | null) => {
      tokenStartRef.current = tokenStart;

      if (!isWeb) {
        router.push({
          pathname: ROUTES.INBOX_REPLY_DRAFT,
          params: { conversationId },
        });

        return;
      }

      setIsOpen(true);
    },
    [conversationId]
  );

  const actionPhrases = useMemo<ActionDotPhrase[]>(
    () =>
      isEnabled
        ? [
            {
              trigger: DRAFT_SHORTCUT.trigger,
              label: DRAFT_SHORTCUT.label,
              description: DRAFT_SHORTCUT.description,
              onRun: open,
            },
          ]
        : NO_PHRASES,
    [isEnabled, open]
  );

  const openFromChip = useCallback(() => open(null), [open]);

  const close = useCallback(() => setIsOpen(false), []);

  const modal: ReplyDraftModalState | null = isEnabled ? { isOpen } : null;

  return {
    actionPhrases,
    placeholderHint: hasOfferedDraft ? DRAFT_SHORTCUT.placeholderHint : null,
    chip: hasOfferedDraft ? { label: DRAFT_SHORTCUT.description, onPress: openFromChip } : null,
    modal,
    close,
  };
}
